import { CURRENCIES, currencyByName, currencySign, type ForeignNorm, type SettingsDto } from '@edms/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { fmtDate } from '../components/fields';
import { api } from '../lib/api';
import { useToast } from '../lib/ui';

type Save = (patch: Partial<SettingsDto>) => void;

const parseNum = (s: string): number | null => {
  const t = s.replace(/[\s ]/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
};
const cleanName = (s: string) =>
  s
    .replace(/<\*+>|\*+|\(\d+\)$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.:;,]+$/, '');

export type PasteMode = 'perDiem' | 'lodging';

interface ParsedRow {
  country: string;
  amount: number;
  currency: string;
}

/**
 * Разбор таблицы, скопированной из текста постановления (Консультант, Гарант, Word, Excel):
 * строка — страна, валюта («доллары США», «евро»…) и сумма; без валюты — доллары США.
 * Строка без чисел — заголовок страны: следующие строки со строчной буквы («прочие города»)
 * относятся к ней. Номер строки в первой колонке пропускается.
 */
export function parseNormsTable(text: string): ParsedRow[] {
  const out: ParsedRow[] = [];
  let parent = '';
  for (const raw of text.split(/\r?\n/)) {
    const cells = raw
      .split(/\t|;|\s{3,}|\|/)
      .map((c) => c.trim())
      .filter(Boolean);
    if (cells.length === 0) continue;
    if (cells.length > 1 && /^\d+\.?$/.test(cells[0]!) && parseNum(cells[1]!) === null) cells.shift();
    const nums = cells.map(parseNum).filter((n): n is number => n !== null);
    const texts = cells.filter((c) => parseNum(c) === null);
    const currencyCell = texts.slice(1).find((c) => currencyByName(c));
    const name = cleanName(texts.filter((c) => c !== currencyCell).join(' '));
    if (!name) continue;
    if (nums.length === 0) {
      parent = name;
      continue;
    }
    const isCity = Boolean(parent) && /^[а-яё]/.test(name);
    const country = isCity ? `${parent} — ${name}` : name;
    if (!isCity) parent = '';
    out.push({ country, amount: nums[0]!, currency: (currencyCell && currencyByName(currencyCell)) || 'USD' });
  }
  return out;
}

const key = (s: string) => s.trim().toLowerCase().replace(/ё/g, 'е');

function merge(norms: ForeignNorm[], rows: ParsedRow[], mode: PasteMode): { norms: ForeignNorm[]; added: number; updated: number } {
  const next = norms.map((n) => ({ ...n }));
  let added = 0;
  let updated = 0;
  for (const r of rows) {
    const patch = mode === 'perDiem' ? { perDiem: r.amount, perDiemCurrency: r.currency } : { lodging: r.amount, lodgingCurrency: r.currency };
    const found = next.find((n) => key(n.country) === key(r.country));
    if (found) {
      Object.assign(found, patch);
      updated += 1;
    } else {
      next.push({ country: r.country, perDiem: null, perDiemCurrency: 'USD', lodging: null, lodgingCurrency: 'USD', ...patch });
      added += 1;
    }
  }
  next.sort((a, b) => a.country.localeCompare(b.country, 'ru'));
  return { norms: next, added, updated };
}

/** Валюты, в которых заданы нормы, — для них нужны курсы. */
function usedCurrencies(norms: ForeignNorm[]): string[] {
  const set = new Set<string>(['USD']);
  for (const n of norms) {
    if (n.perDiem != null) set.add(n.perDiemCurrency);
    if (n.lodging != null) set.add(n.lodgingCurrency);
  }
  return [...set].sort((a, b) => (a === 'USD' ? -1 : b === 'USD' ? 1 : a.localeCompare(b)));
}

function NumberField({ label, value, onSave, step = 50, hint }: { label: string; value: number; onSave: (v: number) => void; step?: number; hint?: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        className="input"
        type="number"
        min={0}
        step={step}
        defaultValue={value}
        onBlur={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v) && v >= 0 && v !== value) onSave(v);
        }}
      />
      {hint && <span className="faint">{hint}</span>}
    </label>
  );
}

const rateText = (v: number | undefined) => (v === undefined ? '' : String(v).replace('.', ','));

function Rates({ s, save }: { s: SettingsDto; save: Save }) {
  const qc = useQueryClient();
  const toast = useToast();
  const codes = usedCurrencies(s['trips.foreign_norms']);
  const [rates, setRates] = useState<Record<string, string>>(() => Object.fromEntries(codes.map((c) => [c, rateText(s['trips.currency_rates'][c])])));
  const [date, setDate] = useState(s['trips.rates_date'] ?? '');
  const refresh = useMutation({
    mutationFn: () => api.post<SettingsDto>('/settings/rates/refresh'),
    onSuccess: (data) => {
      qc.setQueryData(['settings'], data);
      setRates(Object.fromEntries(codes.map((c) => [c, rateText(data['trips.currency_rates'][c])])));
      setDate(data['trips.rates_date'] ?? '');
      toast(`Курсы ЦБ РФ на ${fmtDate(data['trips.rates_date'])} загружены`);
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const parsed = Object.fromEntries(Object.entries(rates).map(([c, v]) => [c, parseNum(v)]));
  const invalid = Object.values(parsed).some((v) => v === null || v <= 0);
  const changed = codes.some((c) => parsed[c] !== (s['trips.currency_rates'][c] ?? null)) || (date || null) !== s['trips.rates_date'];
  const missing = codes.filter((c) => !s['trips.currency_rates'][c]);

  return (
    <div className="card-inset" style={{ display: 'grid', gap: 12 }}>
      <div>
        <b>Курсы валют ЦБ РФ</b>
        <div className="muted">
          {s['trips.rates_date'] ? <>Курсы на {fmtDate(s['trips.rates_date'])}</> : 'Курсы не заданы'}
          {missing.length > 0 && <span className="field-error"> · нет курса: {missing.map((c) => CURRENCIES[c]?.name ?? c).join(', ')}</span>}
        </div>
        <span className="faint">
          Нужны для валют, в которых заданы нормы. При согласовании заграничной командировки суммы в рублях пересчитываются по курсам на этот момент, курсы и их дата
          записываются в заявку.
        </span>
      </div>
      <label className="check">
        <input type="checkbox" checked={s['trips.rates_auto']} onChange={(e) => save({ 'trips.rates_auto': e.target.checked })} />
        <span>
          Обновлять автоматически с сайта ЦБ РФ
          <br />
          <span className="faint">Раз в час проверяются курсы на сегодня. Выключите, если задаёте курсы вручную.</span>
        </span>
      </label>
      <div className="settings-numbers">
        {codes.map((c) => (
          <label key={c} className="field">
            <span>
              1 {currencySign(c)} ({CURRENCIES[c]?.name ?? c}), ₽
            </span>
            <input className="input" inputMode="decimal" value={rates[c] ?? ''} onChange={(e) => setRates((r) => ({ ...r, [c]: e.target.value }))} aria-invalid={rates[c] !== '' && parsed[c] === null} />
          </label>
        ))}
        <label className="field">
          <span>На дату</span>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          disabled={!changed || invalid || !date}
          onClick={() =>
            save({
              'trips.currency_rates': { ...s['trips.currency_rates'], ...(parsed as Record<string, number>) },
              'trips.rates_date': date,
              'trips.rates_auto': false,
            })
          }
        >
          Сохранить курсы вручную
        </button>
        <button className="btn" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? 'Запрашиваем ЦБ…' : 'Загрузить с сайта ЦБ'}
        </button>
      </div>
    </div>
  );
}

function CurrencySelect({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} style={{ maxWidth: 90 }}>
      {Object.keys(CURRENCIES).map((c) => (
        <option key={c} value={c} title={CURRENCIES[c]!.name}>
          {currencySign(c)}
        </option>
      ))}
    </select>
  );
}

function NormsEditor({ s, save }: { s: SettingsDto; save: Save }) {
  const toast = useToast();
  const [norms, setNorms] = useState<ForeignNorm[]>(s['trips.foreign_norms']);
  const [query, setQuery] = useState('');
  const [paste, setPaste] = useState('');
  const [mode, setMode] = useState<PasteMode>('perDiem');
  const [importing, setImporting] = useState(false);
  // База хранит JSON с другим порядком ключей — сравниваем по значениям.
  const canon = (list: ForeignNorm[]) => JSON.stringify(list.map((n) => [n.country, n.perDiem, n.perDiemCurrency, n.lodging, n.lodgingCurrency]));
  const dirty = canon(norms) !== canon(s['trips.foreign_norms']);
  const parsed = useMemo(() => parseNormsTable(paste), [paste]);
  const shown = norms.map((n, i) => ({ n, i })).filter(({ n }) => !query || key(n.country).includes(key(query)));
  const update = (i: number, patch: Partial<ForeignNorm>) => setNorms((list) => list.map((n, j) => (j === i ? { ...n, ...patch } : n)));
  const invalid = norms.some((n) => !n.country.trim());
  const noPerDiem = norms.filter((n) => n.perDiem == null && !n.country.includes(' — ')).length;

  return (
    <div className="card-inset" style={{ display: 'grid', gap: 12 }}>
      <div>
        <b>Нормы для заграничных командировок</b>
        <br />
        <span className="faint">
          Суточные — Приложение N 1 к ПП РФ от 26.12.2005 N 812, 1-й столбец. Проживание — приложение к ПП РФ от 22.08.2020 N 1267. Валюта — как в постановлении. Скопируйте
          таблицу из текста постановления и вставьте кнопкой «Загрузить из таблицы».
        </span>
        {noPerDiem > 0 && <div className="field-error">Без нормы суточных: {noPerDiem} стран — загрузите таблицу ПП № 812</div>}
      </div>
      <div className="toolbar" style={{ padding: 0 }}>
        <input className="input" placeholder={`Поиск среди ${norms.length} стран`} value={query} onChange={(e) => setQuery(e.target.value)} />
        <button className="btn" onClick={() => setImporting((v) => !v)}>
          Загрузить из таблицы
        </button>
        <button className="btn btn-ghost" onClick={() => setNorms((l) => [{ country: '', perDiem: null, perDiemCurrency: 'USD', lodging: null, lodgingCurrency: 'USD' }, ...l])}>
          + страна
        </button>
      </div>

      {importing && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="presets" role="radiogroup" aria-label="Что в таблице">
            {(
              [
                ['perDiem', 'Суточные (ПП № 812)'],
                ['lodging', 'Проживание (ПП № 1267)'],
              ] as const
            ).map(([m, label]) => (
              <button key={m} role="radio" aria-checked={mode === m} className={`tab${mode === m ? ' active' : ''}`} onClick={() => setMode(m)}>
                {label}
              </button>
            ))}
          </div>
          <textarea
            className="textarea"
            rows={6}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder={'Вставьте строки таблицы: страна, валюта, сумма, например:\nАвстрия\tевро\t150\nАзербайджан\tдоллары США\t75'}
          />
          {paste && (
            <>
              <span className="faint">
                Распознано строк: {parsed.length}. Проверьте названия: город с заглавной буквы попадёт отдельной строкой — допишите страну («Германия — Берлин») после
                добавления.
              </span>
              {parsed.length > 0 && (
                <div className="table-wrap" style={{ maxHeight: 200, overflow: 'auto' }}>
                  <table className="table compact">
                    <tbody>
                      {parsed.map((r, i) => (
                        <tr key={i}>
                          <td>{r.country}</td>
                          <td className="num">
                            {r.amount} {currencySign(r.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          <div>
            <button
              className="btn btn-primary"
              disabled={parsed.length === 0}
              onClick={() => {
                const r = merge(norms, parsed, mode);
                setNorms(r.norms);
                setPaste('');
                setImporting(false);
                toast(`Добавлено стран: ${r.added}, обновлено: ${r.updated}. Проверьте и сохраните нормы.`);
              }}
            >
              Добавить в таблицу
            </button>
          </div>
        </div>
      )}

      <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
        <table className="table compact">
          <thead>
            <tr>
              <th>Страна или город</th>
              <th className="num">Суточные в день</th>
              <th className="num">Проживание в сутки</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={4} className="faint">
                  {norms.length === 0 ? 'Нормы не заданы' : 'Ничего не найдено'}
                </td>
              </tr>
            )}
            {shown.map(({ n, i }) => (
              <tr key={i}>
                <td>
                  <input className="input" value={n.country} aria-invalid={!n.country.trim()} onChange={(e) => update(i, { country: e.target.value })} placeholder="Страна" />
                </td>
                <td className="num">
                  <span className="money-cur">
                    <input className="input" inputMode="decimal" value={n.perDiem ?? ''} onChange={(e) => update(i, { perDiem: parseNum(e.target.value) })} aria-label={`Суточные, ${n.country}`} />
                    <CurrencySelect value={n.perDiemCurrency} onChange={(v) => update(i, { perDiemCurrency: v })} label={`Валюта суточных, ${n.country}`} />
                  </span>
                </td>
                <td className="num">
                  <span className="money-cur">
                    <input className="input" inputMode="decimal" value={n.lodging ?? ''} onChange={(e) => update(i, { lodging: parseNum(e.target.value) })} aria-label={`Проживание, ${n.country}`} />
                    <CurrencySelect value={n.lodgingCurrency} onChange={(v) => update(i, { lodgingCurrency: v })} label={`Валюта проживания, ${n.country}`} />
                  </span>
                </td>
                <td>
                  <button className="btn btn-ghost btn-sm" aria-label={`Удалить ${n.country}`} onClick={() => setNorms((l) => l.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          className="btn btn-primary"
          disabled={!dirty || invalid}
          onClick={() => {
            const cleaned = norms.filter((n) => n.country.trim()).map((n) => ({ ...n, country: n.country.trim() }));
            setNorms(cleaned);
            save({ 'trips.foreign_norms': cleaned });
          }}
        >
          Сохранить нормы
        </button>
        {dirty && (
          <button className="btn btn-ghost" onClick={() => setNorms(s['trips.foreign_norms'])}>
            Отменить изменения
          </button>
        )}
        {dirty && <span className="faint">Есть несохранённые изменения</span>}
      </div>
    </div>
  );
}

type ModeKey = 'trips.foreign_per_diem_mode' | 'trips.foreign_lodging_mode';
type RateKey = 'trips.foreign_per_diem_rate' | 'trips.foreign_lodging_rate';

/** За границей: по нормативу страны или единый тариф для всех стран — отдельно для суточных и проживания. */
function ForeignMode({ s, save, label, mode, rate, unit, normHint }: { s: SettingsDto; save: Save; label: string; mode: ModeKey; rate: RateKey; unit: string; normHint: string }) {
  const flat = s[mode] === 'flat';
  return (
    <div className="field foreign-mode">
      <span>{label}</span>
      <div className="foreign-mode-row">
        <select className="select" value={s[mode]} onChange={(e) => save({ [mode]: e.target.value as 'norm' | 'flat' })} aria-label={label}>
          <option value="norm">по нормативу страны</option>
          <option value="flat">единый тариф для всех стран</option>
        </select>
        {flat && (
          <label className="foreign-mode-rate">
            <input
              className="input"
              type="number"
              min={0}
              step={100}
              defaultValue={s[rate]}
              aria-label={`${label}: тариф`}
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= 0 && v !== s[rate]) save({ [rate]: v });
              }}
            />
            <span className="faint">{unit}</span>
          </label>
        )}
      </div>
      <span className="faint">{flat ? 'Курс и норматив страны для этой части не нужны' : `${normHint}, в рубли — по курсу ЦБ РФ`}</span>
    </div>
  );
}

/** Раздел «Командировки» в настройках. */
export function TripSettings({ s, save }: { s: SettingsDto; save: Save }) {
  return (
    <>
      <h2>Командировки</h2>
      <div className="settings-numbers">
        <NumberField label="Суточные по России, ₽ в день" value={s['trips.per_diem_rate']} onSave={(v) => save({ 'trips.per_diem_rate': v })} />
        <NumberField
          label="Проживание по России, ₽ в сутки"
          value={s['trips.lodging_rate']}
          onSave={(v) => save({ 'trips.lodging_rate': v })}
          hint="Ночей на одну меньше, чем дней командировки"
        />
      </div>
      <h3>Заграничные командировки</h3>
      <div className="settings-numbers">
        <ForeignMode s={s} save={save} label="Суточные за границей" mode="trips.foreign_per_diem_mode" rate="trips.foreign_per_diem_rate" unit="₽ в день" normHint="ПП РФ № 812, прил. 1 — норма страны в валюте" />
        <ForeignMode s={s} save={save} label="Проживание за границей" mode="trips.foreign_lodging_mode" rate="trips.foreign_lodging_rate" unit="₽ в сутки" normHint="ПП РФ № 1267 — норма страны в валюте" />
      </div>
      <span className="faint">
        День возвращения в Россию всегда оплачивается по суточным России. Применяется к новым расчётам; поданные заявки пересчитаются при сохранении или запуске,
        согласованные не меняются.
      </span>
      <Rates key={usedCurrencies(s['trips.foreign_norms']).join()} s={s} save={save} />
      <NormsEditor s={s} save={save} />
    </>
  );
}
